/**
 * Ant Design Theme Configuration
 * Imports design tokens from modular files and creates the theme
 */

import {colors} from './colors.module.js';
import {fontSizes, fontWeights, lineHeights} from './fonts.module.js';

export const antdTheme = {
  hashed: false, // prevents class names from being obfuscated, for example .ant-btn-primary instead of .css-abc123
  token: {
    colorPrimary: colors.primaryBlue,

    // backgrounds
    // colorBgElevated is intentionally NOT set globally - it controls Modal, Drawer,
    // Popover, and other elevated surfaces and should remain white (default).
    // The Dropdown component overrides it to gray9 via its own component token below.
    colorBgLayout: colors.gray3,

    colorText: colors.gray9,
    colorTextSecondary: colors.gray8,
    colorTextTertiary: colors.gray7,
    colorTextQuaternary: colors.gray6,

    fontSize: fontSizes.small,
    fontSizeLG: fontSizes.large,
    fontSizeXL: fontSizes.xlarge,
    fontWeightStrong: fontWeights.semiBold,

    // Line heights
    lineHeight: lineHeights.base, // Global base line height (affects most text)

    // v5 adds border-radius by default (rounded corners)
    // v5 defaults: borderRadius: 6, borderRadiusLG: 8, borderRadiusSM: 4, borderRadiusXS: 2
    // To revert to v4 sharp corners, uncomment these overrides:

    // borderRadius: 0,
    // borderRadiusLG: 0,
    // borderRadiusSM: 0,
    // borderRadiusXS: 0,

    // v5 adds box shadows to elevated surfaces by default
    // v5 defaults: boxShadow for Modals, Drawers, Popovers, Tooltips, Dropdowns, Cards
    // To remove shadows and maintain v4 flat design, uncomment these overrides:
    // boxShadow: 'none',
    // boxShadowSecondary: 'none',

    colorRatingExtreme: colors.ratingExtreme,
    colorRatingHigh: colors.ratingHigh,
    colorRatingModerate: colors.ratingModerate,
    colorRatingLow: colors.ratingLow,
    colorRatingMinimal: colors.ratingMinimal,
    colorRatingUnrated: colors.ratingUnrated,
  },
  components: {
    // Breadcrumb hover background added in v5 - remove it (HORIZONWEB-13494)
    // linkColor/separatorColor: our global colorTextTertiary override (#595959) bleeds into
    // colorTextDescription which Breadcrumb uses for link/separator; pin back to the v4 value.
    Breadcrumb: {
      colorBgTextHover: 'transparent',
      linkColor: colors.gray7,
      separatorColor: colors.gray7,
    },

    Dropdown: {
      colorBgElevated: colors.gray1, // white background
      colorText: colors.gray9, // dark text
      colorTextDisabled: colors.gray7,
      controlItemBgHover: colors.gray3, // hover background
      paddingBlock: 5, // top/bottom padding for menu items
    },

    // v5 Card header title changed from 16px/500 weight to 18px/600 weight - restore v4 values
    Card: {
      headerFontSize: fontSizes.normal, // v4: 16px (v5 default: 18px)
      fontWeightStrong: fontWeights.medium, // v4: 500 (v5 default: 600)
    },

    Alert: {
      fontSizeLG: fontSizes.normal, // v4: 16px (inherited global override: 18px)
    },

    // v5 Drawer title changed from 16px/500 weight to 18px/600 weight - restore v4 values
    Drawer: {
      fontSizeLG: fontSizes.normal, // v4: 16px (v5 default: 18px) — controls title font size
      lineHeightLG: lineHeights.xxLarge / fontSizes.normal, // v4: 22px → unitless ratio (~1.375)
      fontWeightStrong: fontWeights.medium, // v4: 500 (v5 default: 600)
      marginXS: 12, // v4 close button margin-right: 12px (v5 default marginXS: 8px)
      colorIcon: colors.gray7, // v4: rgba(0,0,0,0.45) ≈ #8C8C8C = gray7 (v5 default: gray8 #595959)
    },

    // v5 List.Item.Meta changed spacing defaults
    List: {
      itemPaddingLG: '16px 0',
    },

    // v5 Menu changed hover/selected color tokens for dark theme (sidebar)
    // Light mode menu tokens are NOT set here - they would affect horizontal
    // menus (e.g. LocationIntel tabs) globally. Those are scoped via a local
    // ConfigProvider in SideMenu instead.
    Menu: {
      darkItemBg: colors.gray9,
      darkSubMenuItemBg: colors.gray10,
      darkItemColor: colors.gray1A65,
      darkItemSelectedColor: colors.gray1,
      darkItemSelectedBg: colors.gray8,
      darkItemHoverColor: colors.gray1,
      darkItemHoverBg: colors.gray8,
    },

    // v5 PageHeader renamed and adjusted spacing tokens
    PageHeader: {
      paddingMD: 16,
      paddingLG: 24,
    },

    // v5 Descriptions vertical layout increased itemPaddingBottom - restore v4 tighter spacing
    Descriptions: {
      itemPaddingBottom: 4,
    },

    // changed the default sort icon color back to v4's #bfbfbf
    Table: {
      colorIcon: colors.gray6,
    },
  },
};

/**
 * Export all design tokens for use throughout the app
 */
export {colors} from './colors.module.js';
export {fontFamilies, fontSizes, fontWeights, lineHeights} from './fonts.module.js';
export {spacing} from './spacing.module.js';
